import { formList, useForm } from '@mantine/form';
import { useEffect } from 'react';
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const initialValues = {
  // First page
  full_name: '',
  phone: '',
  dob: '',
  specialisation: '',
  experience: '',
  education: '',
  about: '',
  // Video Tariffs
  video: formList([
    {
      name: '',
      duration: '',
      price: '',
      is_active: true,
    }
  ]),
  // Chat Tariffs
  chat: formList([
    {
      name: '',
      duration: '',
      price: '',
      is_active: true,
    },
    {
      name: '',
      duration: '',
      price: '',
      is_active: true,
    },
  ]),
};

const requiredField = (t: any) => (value: any, length: number | void) => {
  if (!value) return t('common.validation.required');
  if (length) {
    if (value.length > length) {
      return t('common.validation.tooLong', { length });
    }
  }
  return value.length < 1 ? t('common.validation.required') : null;
};

const optionalField = (t: any) => (value: any, length: number) => {
  if (!value) return null;
  if (value.length > length) {
    return t('common.validation.tooLong', { length });
  }
  return null;
};

const validatePhoneNumber = (t: any) => (value: string) => {
  if (!value) {
    return t('common.validation.required');
  }
  // Strip all placeholder _ and ()
  const clean = value.replaceAll(/[()\s\-\_]/g, '');
  if (!isPossiblePhoneNumber(clean)) {
    return t('common.validation.wrongPhoneFormat');
  }
  return null;
};

const validateDateOfBirth = (t: any) => (value: string) => {
  if (!value) return t('common.validation.required');
  let parsedDate = dayjs(value, 'DD.MM.YYYY', true);
  if (!parsedDate.isValid()) {
    return t('common.validation.wrongFormat');
  }
  let userAge = dayjs().diff(parsedDate, 'years');
  if (userAge < 18) {
    return t('common.validation.tooYoung');
  }
  return null;
};

type ValidateTariffsParams<T> = T extends 'chat'
  ? typeof initialValues['chat']
  : typeof initialValues['video'];

const validateTariffs = <T>(t: any) => (type: T, tariffs: ValidateTariffsParams<T>) => {
  let errors = {} as any;
  tariffs.forEach((item, index) => {
    const prefix = `${type}.${index}`;
    // Validate only enabled tariff.
    if (item.is_active) {
      if (!item.duration) {
        errors[prefix + '.duration'] = t('common.validation.required');
      }
      if (!item.price) {
        errors[prefix + '.price'] = t('common.validation.required');
      }
      if (Number.isNaN(item.price)) {
        errors[prefix + '.price'] = t('common.validation.wrongFormat');
      }
    }
  });
  return errors;
}

const validateValues = (values: typeof initialValues, t: any) => {
  const chatErrors = validateTariffs<'chat'>(t)('chat', values.chat);
  const videoErrors = validateTariffs<'video'>(t)('video', values.video);
  // The API rejects a submission without an active chat tariff.
  if (!values.chat.some(item => item.is_active)) {
    chatErrors.chat = t('common.validation.chat.atLeastOneTariff');
  }

  return {
    full_name: requiredField(t)(values.full_name),
    phone: validatePhoneNumber(t)(values.phone),
    dob: validateDateOfBirth(t)(values.dob),
    specialisation: requiredField(t)(values.specialisation),
    experience: requiredField(t)(values.experience),
    education: requiredField(t)(values.education, 150),
    about: optionalField(t)(values.about, 1200),
    ...chatErrors,
    ...videoErrors,
  };
};

export const useFormWithConfig = () => {
  const { t } = useTranslation()
  const form =  useForm({
    initialValues,
    validate: values => validateValues(values, t),
  });
  // Mantine form doesn't rerender errors on language change. So we need to update errors manually
  useEffect(() => {
    if (Object.keys(form.errors).length) {
      form.validate()
    }
  }, [t])
  return form
};

export type ConfiguredForm = ReturnType<typeof useFormWithConfig>;
